import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap';
import { releaseVaultedMoney } from 'redux/modules/payments';
import { setSuccessMessage } from 'redux/modules/successMessage';
import { setErrorMessage } from 'redux/modules/errorMessage';
import { endHire } from 'redux/modules/briefCard';
import Label from 'components/Label/Label';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import Avatar from 'components/Avatar/Avatar';
import Verified2 from 'components/Badges/Verified/Verified2';
import Pro from 'components/Badges/Pro/Pro';
import Modal from 'components/Modal/Modal';
import PaymentModal from 'components/Pitch/PaymentModal';
import { formatMoney } from 'helpers/HelperFunctions';
import ActiveHireNavDropdown from '../ActiveHireNavDropdown/ActiveHireNavDropdown';

import styles from '../ActiveHires.module.scss';

const {
  column,
  navDropdownMobile,
  firstColumn,
  firstColumnLink,
  activeHireDisplayName,
  verifiedAndProBox,
  verifiedAndPro,
  messageIconMobile,
  statusLabel,
  amountRequested,
  secondColumn,
  messageClient,
  messageLabel,
  messageIcon,
  thirdColumn,
  mobilePaidOut,
  fourthColumn,
  navDropdownDesktop,
  reviewLink,
} = styles;

const SingleActiveHire = ({
  history,
  releaseVaultedMoney,
  setSuccessMessage,
  activeHire,
  endHire,
  endHireUpdateActiveHires,
  setErrorMessage,
}) => {
  const [showReleasePaymentModal, setShowReleasePaymentModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [isMoneyVaulted, setIsMoneyVaulted] = useState(
    !!activeHire.totalVaulted
  );
  const [totalPaidOut, setTotalPaidOut] = useState(activeHire.totalPaidOut);
  const [showHire, setShowHire] = useState(true);

  const endThisHire = async (briefId, pitchId) => {
    try {
      await endHire(briefId, pitchId);
      setShowHire(false);
      endHireUpdateActiveHires();
    } catch (err) {
      setErrorMessage(`Failed to end hire: ${err.message}`);
      console.error(err);
    }
  };

  const releaseClientsVaultedMoney = (pitchId, totalVaulted) => {
    setShowReleasePaymentModal(false);

    releaseVaultedMoney(pitchId)
      .then(() => {
        setIsMoneyVaulted(false);
        setTotalPaidOut(totalPaidOut + totalVaulted);
        setSuccessMessage('Your money has been released to the freelancer.');
      })
      .catch((err) => {
        alert(`Failed to release money to the freelancer: ${err.message}`);
      });
  };

  const adhocPayment = (briefId, pitchId, grandTotal) => {
    history.push(
      `/manage/briefs/${briefId}/pay/${pitchId}?amount=${grandTotal}&vaultable=true`
    );
  };

  if (!showHire) {
    return <div />;
  }
  const {
    totalVaulted,
    currency,
    freelancerAvatar,
    bills,
    freelancer,
    pitch,
    brief,
    outstandingBilled,
    ratingId,
    ratingUrlCode,
  } = activeHire;

  let billsQueryString = '?bills=';

  bills.forEach((bill) => {
    if (bill.paid_at === null) {
      billsQueryString += `${bill.bill_id},`;
    }
  });
  billsQueryString = billsQueryString.substring(0, billsQueryString.length - 1);

  let leaveReview = '';

  if (ratingUrlCode !== false) {
    const latestRatingUrl = `/testimonials/${ratingId}/${ratingUrlCode}`;
    leaveReview = (
      <Link className={reviewLink} to={latestRatingUrl}>
        Leave review
      </Link>
    );
  }

  const hireIsCompleted = pitch.completed_at && pitch.completed_at !== '';

  // Do not allow 'endHire' if money is vaulted, money is requested, or the hire is completed.
  const canEndHire = !(isMoneyVaulted || outstandingBilled || hireIsCompleted);
  // Do not allow adhoc payments when money is requested
  const canMakePayment = outstandingBilled === 0;

  const amountRequestedString = formatMoney(outstandingBilled, currency);
  const amountVaultedString = formatMoney(totalVaulted, currency);
  const totalPaidOutString = formatMoney(totalPaidOut, currency);
  const messagesLink = `/messages/${freelancer.username}`;
  const displayOrUsername = pitch.users.sender.user_displayname
    ? pitch.users.sender.user_displayname
    : pitch.users.sender.username;

  return (
    <Row>
      <Col sm={5} className={column}>
        <div className={navDropdownMobile}>
          <ActiveHireNavDropdown
            messagesLink={messagesLink}
            id="nav-dropdown-mobile"
            endHire={() => endThisHire(brief.id, pitch.message_id)}
            canMakePayment={canMakePayment}
            canEndHire={canEndHire}
            makePayment={() => setShowPayModal(true)}
          />
        </div>
        <div className={firstColumn + ' mb'}>
          <div className={firstColumnLink}>
            <Link to={`/${freelancer.username}`} className={firstColumnLink}>
              <Avatar image={freelancerAvatar[46].square.main} size={35} />
              <p className={activeHireDisplayName}>{freelancer.displayname}</p>
              <div className={verifiedAndProBox}>
                <Verified2 user={freelancer} className={verifiedAndPro} />
                <Pro user={freelancer} className={verifiedAndPro} />
              </div>
            </Link>
            <Link to={messagesLink}>
              <span className={messageIconMobile}>
                <SvgIcon
                  icon="bubble"
                  color="#40a0f5"
                  size="14"
                  style={{ marginBottom: '5px' }}
                />
              </span>
            </Link>
            {leaveReview}
          </div>
          {!!outstandingBilled && (
            <Label
              className={statusLabel + ' ml ' + amountRequested}
            >{`${amountRequestedString} requested`}</Label>
          )}
          {!!isMoneyVaulted && (
            <Label className={statusLabel + ' ml'} color={'primary'}>
              {`${amountVaultedString} held `}
              <svg
                width="1em"
                height="1em"
                viewBox="0 0 16 16"
                className="bi bi-lock-fill"
                fill="currentColor"
              >
                <path d="M2.5 9a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V9z" />
                <path
                  fillRule="evenodd"
                  d="M4.5 4a3.5 3.5 0 1 1 7 0v3h-1V4a2.5 2.5 0 0 0-5 0v3h-1V4z"
                />
              </svg>
            </Label>
          )}
        </div>
      </Col>
      <Col sm={1} className={column}>
        <div className={`${secondColumn} ${messageClient}`}>
          <Link to={messagesLink} className={`${secondColumn} ${messageLabel}`}>
            <span className={messageIcon}>
              <SvgIcon icon="bubble" color="#40a0f5" size="14" />
            </span>
            Messages
          </Link>
        </div>
      </Col>

      <Col sm={3} className={column}>
        <div className={thirdColumn}>
          <div className={mobilePaidOut}>Paid Out</div>
          {totalPaidOutString}
        </div>
      </Col>
      <Col sm={1} className={column}>
        <div className={fourthColumn}>
          {!!outstandingBilled && (
            <Link
              to={`/manage/briefs/${brief.id}/pay/${pitch.message_id}${billsQueryString}`}
            >
              <Button bsStyle="success">{`Pay ${amountRequestedString}`}</Button>
            </Link>
          )}
          {!!isMoneyVaulted && (
            <Button
              bsStyle="success"
              onClick={() => setShowReleasePaymentModal(true)}
            >{`Release ${amountVaultedString}`}</Button>
          )}
          {!isMoneyVaulted && !outstandingBilled && (
            <Button bsStyle="success" onClick={() => setShowPayModal(true)}>
              New Payment
            </Button>
          )}
        </div>
      </Col>
      <Col sm={1} className={navDropdownDesktop}>
        <ActiveHireNavDropdown
          canMakePayment={canMakePayment}
          messagesLink={messagesLink}
          id="nav-dropdown"
          endHire={() => endThisHire(brief.id, pitch.message_id)}
          canEndHire={canEndHire}
          makePayment={() => setShowPayModal(true)}
        />
      </Col>
      <Modal
        closeButton
        title="Release Payment"
        show={showReleasePaymentModal}
        onExit={() => setShowReleasePaymentModal(false)}
      >
        <PaymentModal
          skipProjectTermsStep
          brief={brief}
          pitch={pitch}
          totalVaulted={totalVaulted}
          onAccept={() =>
            releaseClientsVaultedMoney(pitch.message_id, totalVaulted)
          }
        />
      </Modal>
      <Modal
        closeButton
        show={showPayModal}
        title={`Pay ${displayOrUsername}`}
        onExit={() => setShowPayModal(false)}
      >
        <PaymentModal
          skipProjectTermsStep
          brief={brief}
          pitch={pitch}
          onAccept={(amount) =>
            adhocPayment(brief.id, pitch.message_id, amount * 100)
          }
        />
      </Modal>
    </Row>
  );
};

SingleActiveHire.propTypes = {
  history: PropTypes.object,
  releaseVaultedMoney: PropTypes.func,
  setSuccessMessage: PropTypes.func,
  activeHire: PropTypes.object,
  endHire: PropTypes.func,
  endHireUpdateActiveHires: PropTypes.func,
  setErrorMessage: PropTypes.func,
};

export default withRouter(
  connect(null, {
    releaseVaultedMoney,
    setSuccessMessage,
    setErrorMessage,
    endHire,
  })(SingleActiveHire)
);
